import { APIS, URLS } from '@/shared/config/urls';
import type { PageResponse } from '@/shared/types/abstract-api-response';
import { request } from '@/shared/utils/request';
import type {
  CancelledOrder,
  CheckoutBody,
  CheckoutOrder,
  FinishedOrder,
  OrderDetail,
  OrderHistoryParams,
  OrderHistoryRow,
  OrderPayment,
  OrderTracking,
} from '../types/order.types';

/** Bayar Sekarang: places the order (and registers its shipment with the courier service). A
 *  repeated idempotency key answers the order it made before. */
export const checkout = async (body: CheckoutBody): Promise<CheckoutOrder> => {
  return await request.post(`${URLS.BACK_DSH}${APIS.orderCheckout}`, body);
};

/** The signed-in user's orders, newest first. */
export const fetchOrders = async ({
  page,
  size,
  status,
  transfer_status,
  q,
}: OrderHistoryParams): Promise<PageResponse<OrderHistoryRow>> => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (status) params.set('status', status);
  if (transfer_status) params.set('transfer_status', transfer_status);
  if (q) params.set('q', q);
  return await request.get(`${URLS.BACK_DSH}${APIS.orders(params.toString())}`);
};

/** Lacak Pengiriman: the shipment and its courier scans (the backend refreshes them from the courier
 *  service at most every 15 minutes). */
export const fetchOrderTracking = async (orderId: string): Promise<OrderTracking> => {
  return await request.get(`${URLS.BACK_DSH}${APIS.orderTracking(orderId)}`);
};

/** Diterima. 422 until every parcel is delivered; repeating it is fine. */
export const finishOrder = async (orderId: string): Promise<FinishedOrder> => {
  return await request.post(`${URLS.BACK_DSH}${APIS.orderFinish(orderId)}`);
};

/** Batalkan an unpaid order (its courier order first; 422 when the courier refuses). Repeating is fine. */
export const cancelOrder = async (orderId: string): Promise<CancelledOrder> => {
  return await request.post(`${URLS.BACK_DSH}${APIS.orderCancel(orderId)}`);
};

/** One of the signed-in user's orders, with its payment (the transfer instructions while unpaid). */
export const fetchOrder = async (orderId: string): Promise<OrderDetail> => {
  return await request.get(`${URLS.BACK_DSH}${APIS.order(orderId)}`);
};

/** Saya sudah transfer, to the store account picked: the admin checks that account's statement for the
 *  amount. Repeating is fine (the account first reported is kept); 400 for an account no longer offered,
 *  422 when the order no longer waits for payment. */
export const reportTransfer = async (
  orderId: string,
  bankAccountId: string
): Promise<OrderPayment> => {
  return await request.post(`${URLS.BACK_DSH}${APIS.orderReportTransfer(orderId)}`, {
    bank_account_id: bankAccountId,
  });
};
